/**
 * Activity gate for the Site page's decision-analysis progress poll
 * (`GET /api/decision-analyses/:siteId` re-read via `analysisOpsNonce`).
 *
 * The poll exists only to keep a RUNNING refresh's stage/progress live, so it
 * runs only while at least one analysis is queued or refreshing (the matrix
 * renders `refreshing` as "generating"), or a just-accepted refresh is still
 * bridged optimistically in `pendingRefreshByType`. With nothing running there
 * is no interval poll at all; one-off refetches (site switch, auth/owner
 * change) are unaffected because they key the fetch effect directly.
 *
 * A RETIRED domain is excluded: it has no writer (the dispatch gate refuses
 * the only job that could move its pointer), so a pointer frozen at
 * `queued`/`refreshing` can never progress. The page already hides its runtime
 * overlay; before this gate it also kept the poll alive forever.
 */

export const ANALYSIS_OPS_POLL_INTERVAL_MS = 12_000;

/**
 * A pointer read older than this no longer blocks a poll tick. The route's
 * p95 was ~11 s under load; five intervals is far beyond a healthy read but
 * keeps a hung request from freezing progress indefinitely.
 */
export const ANALYSIS_OPS_STALE_IN_FLIGHT_MS = 60_000;

const RUNNING_REFRESH_STATES: ReadonlySet<string> = new Set(['queued', 'refreshing']);

export interface AnalysisOpsPollInput<Domain extends string> {
  operations: ReadonlyArray<{ domain: Domain; refreshState?: string }>;
  /** Optimistic just-accepted refreshes, keyed by domain/report type. */
  pendingRefreshByType: Readonly<Partial<Record<string, number>>>;
  /** Domains that can never progress (retired report types). */
  isRetiredDomain?: (domain: Domain) => boolean;
}

export function shouldPollAnalysisOperations<Domain extends string>(
  input: AnalysisOpsPollInput<Domain>,
): boolean {
  if (Object.keys(input.pendingRefreshByType).length > 0) return true;
  return input.operations.some(operation => (
    operation.refreshState !== undefined
    && RUNNING_REFRESH_STATES.has(operation.refreshState)
    && !input.isRetiredDomain?.(operation.domain)
  ));
}
